<script lang="ts" setup>
import { computed } from 'vue'

import { PSpacePrimitive, PSpaceSemantic, PSpaceSize } from '../space/types'
import { PFlexAlign, PFlexDirection, PFlexJustify } from './types'

type Props = {
  gap?: PSpaceSize | PSpaceSemantic | PSpacePrimitive
  direction?: PFlexDirection
  directionLaptop?: PFlexDirection | null
  directionDesktop?: PFlexDirection | null
  justifyContent?: PFlexJustify
  justifyContentLaptop?: PFlexJustify | null
  justifyContentDesktop?: PFlexJustify | null
  alignItems?: PFlexAlign | null
  alignItemsLaptop?: PFlexAlign | null
  alignItemsDesktop?: PFlexAlign | null
  isScrollable?: boolean
  isScrollableLaptop?: boolean | null
  isScrollableDesktop?: boolean | null
}

const props = withDefaults(defineProps<Props>(), {
  gap: PSpacePrimitive.SPACE_008,
  direction: PFlexDirection.ROW,
  directionLaptop: null,
  directionDesktop: null,
  justifyContent: PFlexJustify.START,
  justifyContentLaptop: null,
  justifyContentDesktop: null,
  alignItems: null,
  alignItemsLaptop: null,
  alignItemsDesktop: null,
  isScrollable: false,
  isScrollableLaptop: null,
  isScrollableDesktop: null,
})

const directionLaptop = computed<PFlexDirection>(() => props.directionLaptop ?? props.direction)

const directionDesktop = computed<PFlexDirection>(
  () => props.directionDesktop ?? directionLaptop.value,
)

const justifyContentLaptop = computed<PFlexJustify>(
  () => props.justifyContentLaptop ?? props.justifyContent,
)

const justifyContentDesktop = computed<PFlexJustify>(
  () => props.justifyContentDesktop ?? justifyContentLaptop.value,
)

// A column stacks the items, so by default it spreads them across the full width,
// while a row keeps the fields centered on their shared baseline.
const alignItemsFor = (direction: PFlexDirection): PFlexAlign =>
  direction === PFlexDirection.COLUMN ? PFlexAlign.STRETCH : PFlexAlign.CENTER

const alignItems = computed<PFlexAlign>(() => props.alignItems ?? alignItemsFor(props.direction))

const alignItemsLaptop = computed<PFlexAlign>(
  () => props.alignItemsLaptop ?? props.alignItems ?? alignItemsFor(directionLaptop.value),
)

const alignItemsDesktop = computed<PFlexAlign>(
  () =>
    props.alignItemsDesktop ??
    props.alignItemsLaptop ??
    props.alignItems ??
    alignItemsFor(directionDesktop.value),
)

const isScrollableLaptop = computed<boolean>(() => props.isScrollableLaptop ?? props.isScrollable)

const isScrollableDesktop = computed<boolean>(
  () => props.isScrollableDesktop ?? isScrollableLaptop.value,
)

// A row and a visible overflow are the browser defaults, so only the exceptions get a class.
const classesFor = (
  breakpoint: string,
  direction: PFlexDirection,
  justifyContent: PFlexJustify,
  alignItems: PFlexAlign,
  isScrollable: boolean,
): string[] => [
  `ds-flex-row-justify-${breakpoint}-${justifyContent}`,
  `ds-flex-row-align-${breakpoint}-${alignItems}`,
  ...(direction === PFlexDirection.COLUMN ? [`ds-flex-row-column-${breakpoint}`] : []),
  ...(isScrollable ? [`ds-flex-row-scroll-${breakpoint}`] : []),
]

const classes = computed<string[]>(() => [
  `ds-flex-row-gap-${props.gap}`,
  ...classesFor(
    'mobile',
    props.direction,
    props.justifyContent,
    alignItems.value,
    props.isScrollable,
  ),
  ...classesFor(
    'laptop',
    directionLaptop.value,
    justifyContentLaptop.value,
    alignItemsLaptop.value,
    isScrollableLaptop.value,
  ),
  ...classesFor(
    'desktop',
    directionDesktop.value,
    justifyContentDesktop.value,
    alignItemsDesktop.value,
    isScrollableDesktop.value,
  ),
])
</script>

<template>
  <div :class="classes" class="ds-flex-row">
    <slot />
  </div>
</template>

<style lang="scss" scoped>
@import '../../assets/scss/shared/ds.variables.module';
@import '../../assets/scss/shared/mixins';

.ds-flex-row {
  display: flex;
  flex-wrap: nowrap;
  min-width: 0;
  gap: var(--ds-flex-row-gap, 0);
}

$justifications: (
  start: flex-start,
  end: flex-end,
  center: center,
  between: space-between,
  around: space-around,
  evenly: space-evenly,
);

$alignments: (
  start: flex-start,
  end: flex-end,
  center: center,
  stretch: stretch,
  baseline: baseline,
);

@mixin breakpoint-rules($breakpoint) {
  @each $name, $value in $justifications {
    .ds-flex-row-justify-#{$breakpoint}-#{$name} {
      justify-content: $value;
    }
  }

  @each $name, $value in $alignments {
    .ds-flex-row-align-#{$breakpoint}-#{$name} {
      align-items: $value;
    }
  }

  .ds-flex-row-column-#{$breakpoint} {
    flex-direction: column;
  }

  .ds-flex-row-scroll-#{$breakpoint} {
    overflow: auto;
  }
}

@include mobile {
  @include breakpoint-rules('mobile');
}

@include laptop {
  @include breakpoint-rules('laptop');
}

@include desktop {
  @include breakpoint-rules('desktop');
}

// The gap is exposed as a custom property, so that a pinned PFlexItem can paint over it.
@each $name, $space in $spaces {
  .ds-flex-row-gap-#{$name} {
    @include mobile {
      --ds-flex-row-gap: #{map-get($space, 'mobile')};
    }

    @include laptop {
      --ds-flex-row-gap: #{map-get($space, 'laptop')};
    }

    @include desktop {
      --ds-flex-row-gap: #{map-get($space, 'desktop')};
    }
  }
}

@each $name, $space in $semantic-spaces {
  .ds-flex-row-gap-#{$name} {
    @include mobile {
      --ds-flex-row-gap: var(--space-#{$name}-mobile);
    }

    @include laptop {
      --ds-flex-row-gap: var(--space-#{$name}-laptop);
    }

    @include desktop {
      --ds-flex-row-gap: var(--space-#{$name}-desktop);
    }
  }
}

@each $name, $space in $space-primitives {
  .ds-flex-row-gap-#{$name} {
    --ds-flex-row-gap: var(--#{$name});
  }
}
</style>
